import { getStageState } from "../lib/journey";
import { track, BETA_SECRET } from "../lib/api";
import DocVerification from "../components/DocVerification";
import PlaceholderPhoto from "../components/PlaceholderPhoto";

// Stage 7 — MLS & syndication.
// v1: status flow with a pre-flight check that listing copy is approved.
// Future: AI normalizes the listing into the destination MLS's field schema
// and validates before submit.

const PORTALS = [
  "Regional MLS (broker-of-record)",
  "Zillow",
  "Redfin",
  "Trulia",
  "Realtor.com",
  "Homes.com",
];

export default function MlsStage({ state, journey, update }) {
  const listingState = getStageState(journey, "listing");
  const listingReady = listingState.status === "done" && listingState.data?.body;
  const verified = Boolean(state.data?.verified_at);
  const photos = getStageState(journey, "photography").data?.photos || [];

  const submit = () => {
    if (!listingReady || !verified) return;
    update({
      status: "done",
      data: { ...(state.data || {}), submitted_at: new Date().toISOString(), portals: PORTALS },
    });
    track("seller_mls_submitted");
    // Publish to the buyer-visible listings table — best-effort, never
    // blocks the seller's stage completion (matches "seamless": background
    // actions don't gate the UI). journey.id === seller_listings.id.
    fetch("/api/listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
      body: JSON.stringify({ action: "publish", seller_listing_id: journey.id }),
    }).catch(() => {});
  };

  const submitted = state.status === "done";
  const ownerName = journey.verification?.owner_of_record || null;

  return (
    <>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
          Listing photos
        </div>
        {photos.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", gap: 8 }}>
            {photos.map((url) => (
              <div key={url} style={{ aspectRatio: "1", borderRadius: 10, overflow: "hidden", border: "1.5px solid #d8ede6" }}>
                <img src={url} alt="listing" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8 }}>
              <PlaceholderPhoto label="exterior" />
              <PlaceholderPhoto label="living" />
              <PlaceholderPhoto label="kitchen" />
              <PlaceholderPhoto label="bedroom" />
            </div>
            <p style={{ fontSize: 12, color: "#7aad98", marginTop: 8 }}>
              No photos uploaded yet — add them from the Photography step and they'll show up here automatically.
            </p>
          </>
        )}
      </div>

      <div className="s-ai-pill" style={{ marginBottom: 14 }}>★ Roadmap: real MLS filing + syndication to Zillow, Redfin & other portals — requires broker-of-record MLS credentials we don't have yet</div>

      <div className="s-soon" style={{ marginBottom: 18 }}>
        <div className="s-soon-row">
          <div className="s-soon-bullet">✓</div>
          <div className="s-soon-text"><strong>Live on sixZero.</strong> Your listing appears in sixZero's own buyer search immediately, with your real disclosures and photos.</div>
        </div>
        <div className="s-soon-row">
          <div className="s-soon-bullet">→</div>
          <div className="s-soon-text"><strong>Regional MLS filing</strong> (Zillow, Redfin, Trulia, Realtor.com, Homes.com reach) is not yet automated — it requires MLS board membership we're working on. For now, submitting here does not put you on those sites.
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {PORTALS.map((p) => (
                <span key={p} style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#5b7365", background: "#fff", border: "1px solid #d8ede6", borderRadius: 8, padding: "3px 8px" }}>{p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!listingReady && (
        <div style={{ background: "#fef4e8", border: "1px solid #f1d6a8", borderRadius: 10, padding: 12, color: "#c47c1a", fontSize: 13, marginBottom: 12 }}>
          Listing copy not ready yet — finish the Listing step first.
        </div>
      )}

      {/* Ownership verification gate — required before MLS publish. */}
      {listingReady && !verified && !submitted && (
        <>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            Verify ownership before publish
          </div>
          <DocVerification
            type="ownership"
            journey={journey}
            expectedName={ownerName}
            expectedAddress={journey.address}
            label="Upload proof of ownership"
            helpText="Property tax bill, deed, mortgage statement, or utility bill. We extract the name + address and match against public records."
            onComplete={(result) => {
              if (result.verified) {
                update({ data: { ...(state.data || {}), verified_at: new Date().toISOString(), verification_id: result.verification_id } });
                track("seller_mls_ownership_verified");
              }
            }}
          />
        </>
      )}

      {listingReady && verified && !submitted && (
        <>
          <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 12, fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, background: "#e4f7ef", color: "#2d7a62", border: "1px solid #b8e8d4" }}>
            ✓ Ownership verified
          </div>
          <div>
            <button type="button" className="s-btn amber" onClick={submit}>
              Publish listing →
            </button>
          </div>
        </>
      )}

      {submitted && (
        <p style={{ fontSize: 12, color: "#2d7a62", fontFamily: "'DM Mono',monospace", marginTop: 6 }}>
          ✓ live on sixZero
        </p>
      )}
    </>
  );
}
